import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { YoutubePlayerModule } from 'ngx-youtube-player';
import { FacebookModule } from 'ngx-facebook';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
  CovalentFileModule,
  CovalentDialogsModule,
  CovalentLoadingModule
} from '@covalent/core';

import {
  MatIconModule,
  MatToolbarModule,
  MatCardModule,
  MatPaginatorIntl,
  MatPaginatorModule,
  MatDialogModule,
  MatTooltipModule,
  MatSelectModule,
  MatButtonModule,
  MatInputModule,
  MatMenuModule,
  MatCheckboxModule,
  MatNativeDateModule,
  MatDatepickerModule,
  ErrorStateMatcher,
  ShowOnDirtyErrorStateMatcher,
  MatExpansionModule,
  MatDividerModule,
  MatListModule
} from '@angular/material';

// Pipes
import { TrimPipe } from './pipes/trim.pipe';
import { ChunkPipe } from './pipes/chunk.pipe';
import { HighlightPipe } from './pipes/highlight.pipe';
import { TruncatePipe } from './pipes/truncate.pipe';
import { AutolinkerPipe } from './pipes/autolinker.pipe';

// Directives
import { ScrollRevealDirective } from './directives/scroll-reveal.directive';

// Services
import { IdeaService } from './services/idea.service';
import { AuthService } from './services/auth.service';
import { AuthGuard } from './services/auth-guard.service';
import { SessionDataResolver } from './services/session-data-resolver.service';
import { MetaTagsService } from './services/meta-tags.service';
import { AdminService } from './services/admin/admin.service';

// Modules
import { AppRoutingModule } from './app-routing.module';

// Components
import { AppComponent } from './app.component';
import { HomeComponent } from './components/home/home.component';
import { ProfileComponent } from './components/profile/profile.component';
import { AuthComponent } from './components/auth/auth.component';
import { VerifyComponent } from './components/auth/components/verify/verify.component';
import { ResetPasswordComponent } from './components/auth/components/reset-password/reset-password.component';
import { InstructionsComponent } from './components/instructions/instructions.component';
import { CategoryListComponent } from './components/category-list/category-list.component';
import { CategoryDetailsComponent } from './components/category-details/category-details.component';
import { IdeaListComponent } from './components/idea-list/idea-list.component';
import { IdeaListMyComponent } from './components/idea-list-my/idea-list-my.component';
import { IdeaDetailsComponent } from './components/idea-details/idea-details.component';
import { IdeaCreateComponent } from './components/idea-create/idea-create.component';
import { IdeaCreateDialogComponent } from './components/idea-create-dialog/idea-create-dialog.component';
import { IdeaSignDialogComponent } from './components/idea-sign-dialog/idea-sign-dialog.component';
import { IdeaSearchPanelComponent } from './components/idea-search-panel/idea-search-panel.component';
import { AuthorListComponent } from './components/author-list/author-list.component';
import { AuthorDetailsComponent } from './components/author-details/author-details.component';
import { ParticipantListDialogComponent } from './components/participant-list-dialog/participant-list-dialog.component';
import { DeadlineDialogComponent } from './components/deadline-dialog/deadline-dialog.component';
import { DecisionDialogComponent } from './components/decision-dialog/decision-dialog.component';
import { MessageDialogComponent } from './components/message-dialog/message-dialog.component';
import { YoutubeVideoDialogComponent } from './components/youtube-video-dialog/youtube-video-dialog.component';
import { RulesDialogComponent } from './components/rules-dialog/rules-dialog.component';
import { SvgUseComponent } from './components/svg-use/svg-use.component';
import { TokenClientIdInterceptorService } from './shared/token-client-id-interceptor.service';
import { AdminHeaderComponent } from './components/admin/components/admin-header/admin-header.component';
import { AdminComponent } from './components/admin/admin.component';
import { AdminNavigationComponent } from './components/admin/components/admin-navigation/admin-navigation.component';
import { AdminIdeaNewComponent } from './components/admin/components/admin-idea-new/admin-idea-new.component';
import { AdminIdeaDefectedComponent } from './components/admin/components/admin-idea-defected/admin-idea-defected.component';
import { AdminIdeaInDiscussionComponent } from './components/admin/components/admin-idea-in-discussion/admin-idea-in-discussion.component';
import { AdminIdeaCurrentComponent } from './components/admin/components/admin-idea-current/admin-idea-current.component';
import { AdminIdeaCompletedComponent } from './components/admin/components/admin-idea-completed/admin-idea-completed.component';
import { AdminIdeaFailedComponent } from './components/admin/components/admin-idea-failed/admin-idea-failed.component';
import { AdminIdeaAgreedComponent } from './components/admin/components/admin-idea-agreed/admin-idea-agreed.component';
import { AdminIdeaRejectedComponent } from './components/admin/components/admin-idea-rejected/admin-idea-rejected.component';
import { AdminIdeaBlockedComponent } from './components/admin/components/admin-idea-blocked/admin-idea-blocked.component';
import { AdminSeeMessageDialogComponent } from './components/admin/components/dialogs/admin-see-message-dialog/admin-see-message-dialog.component';
import { AdminMovementHistoryDialogComponent } from './components/admin/components/dialogs/admin-movement-history-dialog/admin-movement-history-dialog.component';
import { AdminIdeaWaitDecisionComponent } from './components/admin/components/admin-idea-wait-decision/admin-idea-wait-decision.component';
import { AdminIdeaSentDiscussComponent } from './components/admin/components/admin-idea-sent-discuss/admin-idea-sent-discuss.component';
import { AdminIdeaAllComponent } from './components/admin/components/admin-idea-all/admin-idea-all.component';
import { AdminGridItemComponent } from './components/admin/components/shared/admin-grid-item/admin-grid-item.component';
import { PaginatorComponent } from './components/admin/components/shared/paginator/paginator.component';
import { PetitionDetailsComponent } from './components/admin/components/shared/petition-details/petition-details.component';
import { ForbiddenComponent } from './components/admin/components/forbidden/forbidden.component';
import { NotificationDialogComponent } from './components/admin/components/shared/petition-details/dialogs/notification-dialog/notification-dialog.component';
import { ReviewResultsDialogComponent } from './components/admin/components/shared/petition-details/dialogs/review-results-dialog/review-results-dialog.component';
import { AgreementDialogComponent } from './components/admin/components/shared/petition-details/dialogs/agreement-dialog/agreement-dialog.component';
import { RefusalDialogComponent } from './components/admin/components/shared/petition-details/dialogs/refusal-dialog/refusal-dialog.component';
import { MovementHistoryDialogComponent } from './components/admin/components/shared/petition-details/dialogs/movement-history-dialog/movement-history-dialog.component';
import { AuthorInfoDialogComponent } from './components/admin/components/shared/petition-details/dialogs/author-info-dialog/author-info-dialog.component';
import { SupportersListDialogComponent } from './components/admin/components/shared/petition-details/dialogs/supporters-list-dialog/supporters-list-dialog.component';
import { ImageDialogComponent } from './components/admin/components/shared/petition-details/dialogs/image-dialog/image-dialog.component';
import { FilterComponent } from './components/admin/components/admin-idea-all/filter/filter.component';
import { MatTableModule } from '@angular/material/table';
import { DialogsHeaderComponent } from './components/admin/components/shared/petition-details/dialogs/dialogs-header/dialogs-header.component';
import { DecisionsTransformService } from './services/admin/decisions-transform.service';
import { ConfirmationDialogComponent } from './components/admin/components/shared/petition-details/dialogs/review-results-dialog/dialogs/confirmation-dialog/confirmation-dialog.component';
import { AddEmployeeDialogComponent } from './components/admin/components/shared/petition-details/dialogs/review-results-dialog/dialogs/add-employee-dialog/add-employee-dialog.component';
import { SelectMunicipalityDialogComponent } from './select-municipality/select-municipality-dialog/select-municipality-dialog.component';
import { SelectMunicipalityComponent } from './select-municipality/select-municipality.component';
import { MunicipalityService } from './services/municipality.service';
import { DescriptionDialogComponent } from './components/admin/components/shared/petition-details/dialogs/description-dialog/description-dialog.component';
import { StartVoteDialogComponent } from './components/admin/components/shared/petition-details/dialogs/start-vote-dialog/start-vote-dialog.component';
import { DefectDialogComponent } from './components/admin/components/shared/petition-details/dialogs/defect-dialog/defect-dialog.component';
import { BlockDialogComponent } from './components/admin/components/shared/petition-details/dialogs/block-dialog/block-dialog.component';
import { PrepateAnswerDialogComponent } from './components/admin/components/shared/petition-details/dialogs/prepate-answer-dialog/prepate-answer-dialog.component';
import { SendForDiscussDialogComponent } from './components/admin/components/shared/petition-details/dialogs/send-for-discuss-dialog/send-for-discuss-dialog.component';
import { ViewMayorCommentDialogComponent } from './components/admin/components/shared/petition-details/dialogs/view-mayor-comment-dialog/view-mayor-comment-dialog.component';
import { ExecutorAnswerDialogComponent } from './components/admin/components/shared/petition-details/dialogs/executor-answer-dialog/executor-answer-dialog.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AdminIdeaResponseModeComponent } from './components/admin/components/admin-idea-response-mode/admin-idea-response-mode.component';
import { AdminIdeaMyArchiveComponent } from './components/admin/components/admin-idea-my-archive/admin-idea-my-archive.component';
import { AdminIdeaInDiscussComponent } from './components/admin/components/admin-idea-in-discuss/admin-idea-in-discuss.component';
import { AdminIdeaResponseComponent } from './components/admin/components/admin-idea-response/admin-idea-response.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslatePipe } from './pipes/translations.pipe';
import { TranslationsService } from './services/admin/translations.servise';
import { SharedService } from './services/shared.service';
import { CategoriesPanelComponent } from './components/admin/components/categories-panel/categories-panel.component';
import { AddEditCategoryDialogComponent } from './components/admin/components/categories-panel/dialogs/add-edit-category-dialog/add-edit-category-dialog.component';
import { ConfigPanelComponent } from './components/admin/components/config-panel/config-panel.component';
import { VerificationService } from './services/verification.service';
import { RedirectFromAppsComponent } from './components/admin/components/shared/redirect-from-apps/redirect-from-apps.component';
import { OldProfileComponent } from './components/old-profile/profile.component';
import { AdminAuthGuard } from './components/admin/guards/admin-auth.guard';
import { FooterComponent } from './components/footer/footer.component';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MsdaSideNavService } from './msda-sidenav/service/msda-sidenav.service';
import { SelectClientComponent } from './msda-sidenav/components/select-client/select-client.component';
import { MsdaSidenavComponent } from './msda-sidenav/components/msda-sidenav.component';
import { PublicApiUrlInterceptor } from './interceptors/public-api-url.interceptor';

export class MatPaginatorIntlCro extends MatPaginatorIntl {
  itemsPerPageLabel = 'ჩანაწერების რაოდენობა გვერდზე';
  previousPageLabel = 'წინა გვერდი';
  nextPageLabel = 'შემდეგი გვერდი';
}

export function initApp(http: MunicipalityService) {
  return () => {
    return http.load();
  };
}
const initAppforUm = () => {
  return async () => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    const clientId = searchParams.get('clientId');
    const adminUrl = document.location.href.includes('admin');
    const tokenType = adminUrl ? 'private-token' : 'public-token';

    if (token && token !== 'null') {
      localStorage.setItem(tokenType, String(token));
    }
    if (clientId && clientId !== 'null') {
      localStorage.setItem('clientId', String(clientId));
      sessionStorage.setItem('clientId', String(clientId));
    }
    return;
  };
};
/*export function initAppTranslation(http: HttpClient) {
  return async () => {
      const [dictionary, translations] = await Promise.all([
          http.get('/api/docflow/dictionary').toPromise(),
          http.post<any>('/api/translations', { data: {} }).toPromise()])
      localStorage.setItem('dictionary', JSON.stringify((dictionary as any).result.data));
      localStorage.setItem('translations', JSON.stringify((translations as any).result.data));

  };
}*/
@NgModule({
  declarations: [
    TranslatePipe,
    TrimPipe,
    ChunkPipe,
    HighlightPipe,
    TruncatePipe,
    AutolinkerPipe,
    ScrollRevealDirective,
    AppComponent,
    HomeComponent,
    ProfileComponent,
    OldProfileComponent,
    AuthComponent,
    AdminComponent,
    AdminHeaderComponent,
    AdminNavigationComponent,
    AdminIdeaNewComponent,
    VerifyComponent,
    ResetPasswordComponent,
    InstructionsComponent,
    CategoryListComponent,
    CategoryDetailsComponent,
    IdeaListComponent,
    IdeaListMyComponent,
    IdeaDetailsComponent,
    IdeaCreateComponent,
    IdeaCreateDialogComponent,
    IdeaSignDialogComponent,
    IdeaSearchPanelComponent,
    AuthorListComponent,
    AuthorDetailsComponent,
    ParticipantListDialogComponent,
    DeadlineDialogComponent,
    DecisionDialogComponent,
    MessageDialogComponent,
    AdminIdeaDefectedComponent,
    AdminIdeaInDiscussionComponent,
    AdminIdeaCurrentComponent,
    AdminIdeaCompletedComponent,
    AdminIdeaFailedComponent,
    AdminIdeaAgreedComponent,
    AdminIdeaRejectedComponent,
    AdminIdeaBlockedComponent,
    AdminSeeMessageDialogComponent,
    AdminMovementHistoryDialogComponent,
    AdminIdeaWaitDecisionComponent,
    AdminIdeaSentDiscussComponent,
    AdminIdeaMyArchiveComponent,
    AdminIdeaResponseComponent,
    AdminIdeaAllComponent,
    YoutubeVideoDialogComponent,
    RulesDialogComponent,
    NotificationDialogComponent,
    ReviewResultsDialogComponent,
    AgreementDialogComponent,
    RefusalDialogComponent,
    MovementHistoryDialogComponent,
    AuthorInfoDialogComponent,
    SupportersListDialogComponent,
    ImageDialogComponent,
    FilterComponent,
    SvgUseComponent,
    DialogsHeaderComponent,
    ConfirmationDialogComponent,
    PaginatorComponent,
    PetitionDetailsComponent,
    SelectMunicipalityDialogComponent,
    SelectMunicipalityComponent,
    DescriptionDialogComponent,
    StartVoteDialogComponent,
    DefectDialogComponent,
    BlockDialogComponent,
    PrepateAnswerDialogComponent,
    SendForDiscussDialogComponent,
    ViewMayorCommentDialogComponent,
    ExecutorAnswerDialogComponent,
    AdminIdeaInDiscussComponent,
    AdminIdeaResponseModeComponent,
    CategoriesPanelComponent,
    AddEditCategoryDialogComponent,
    ConfigPanelComponent,
    RedirectFromAppsComponent,
    ForbiddenComponent,
    AddEmployeeDialogComponent,
    AdminGridItemComponent,
    FooterComponent,
    SelectClientComponent,
    MsdaSidenavComponent
  ],
  imports: [
    MatSidenavModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    YoutubePlayerModule,
    FacebookModule.forRoot(),
    MatIconModule,
    MatToolbarModule,
    MatCardModule,
    MatPaginatorModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    MatTableModule,
    MatMenuModule,
    MatCheckboxModule,
    MatNativeDateModule,
    MatDatepickerModule,
    CovalentFileModule,
    CovalentDialogsModule,
    CovalentLoadingModule,
    MatExpansionModule,
    AppRoutingModule,
    MatDividerModule,
    MatListModule,
    MatProgressBarModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    MatSidenavModule
  ],
  providers: [
    MsdaSideNavService,
    IdeaService,
    AuthService,
    VerificationService,
    AdminService,
    AuthGuard,
    AdminAuthGuard,
    SessionDataResolver,
    MetaTagsService,
    DecisionsTransformService,
    MunicipalityService,
    TranslationsService,
    SharedService,
    {
      provide: APP_INITIALIZER,
      useFactory: initAppforUm,
      multi: true,
      deps: []
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      multi: true,
      deps: [MunicipalityService]
    },
    /*{
          provide: APP_INITIALIZER,
          useFactory: initAppTranslation,
          multi: true,
          deps: [HttpClient]
        },*/
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorIntlCro
    },
    {
      provide: ErrorStateMatcher,
      useClass: ShowOnDirtyErrorStateMatcher
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenClientIdInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PublicApiUrlInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    AddEditCategoryDialogComponent,
    IdeaCreateDialogComponent,
    IdeaSignDialogComponent,
    ParticipantListDialogComponent,
    DeadlineDialogComponent,
    DecisionDialogComponent,
    MessageDialogComponent,
    YoutubeVideoDialogComponent,
    RulesDialogComponent,
    AdminSeeMessageDialogComponent,
    AdminMovementHistoryDialogComponent,
    NotificationDialogComponent,
    ReviewResultsDialogComponent,
    AgreementDialogComponent,
    RefusalDialogComponent,
    MovementHistoryDialogComponent,
    AuthorInfoDialogComponent,
    SupportersListDialogComponent,
    ImageDialogComponent,
    AddEmployeeDialogComponent,
    ConfirmationDialogComponent,
    SelectMunicipalityDialogComponent,
    DescriptionDialogComponent,
    StartVoteDialogComponent,
    DefectDialogComponent,
    BlockDialogComponent,
    PrepateAnswerDialogComponent,
    SendForDiscussDialogComponent,
    ViewMayorCommentDialogComponent,
    ExecutorAnswerDialogComponent,
    SelectClientComponent
  ]
})
export class AppModule {
  public static imagesSourceUrl: string = `https://sso.municipal.gov.ge`;
  public static isPrivate: boolean | undefined;
}



// WEBPACK FOOTER //
// /opt/app-root/src/src/app/app.module.ts